import { SystemError } from '../../errors';

export type SearchState = 'ok' | 'empty' | 'unavailable' | 'forbidden';

export interface SearchRow {
  readonly entity: string;
  readonly id: number;
  readonly title: string;
  readonly subtitle?: string;
  readonly href: string;
}

export interface AgentRow {
  readonly entity: string;
  readonly id: number;
  readonly label: string;
  readonly matchSource: 'keyword' | 'semantic';
  readonly fields: Readonly<Record<string, string | number | null>>;
}

type Doc = Readonly<Record<string, unknown>>;
type FieldValue = string | number | null;

interface EntitySpec {
  readonly idKey: string;
  readonly title: (doc: Doc) => string | undefined;
  readonly subtitle: (doc: Doc) => string | undefined;
  readonly label: (title: string, doc: Doc) => string;
  readonly href: (doc: Doc) => string;
  readonly fields: Readonly<Record<string, (doc: Doc) => FieldValue>>;
}

const asDoc = (doc: unknown): Doc => (typeof doc === 'object' && doc !== null ? (doc as Doc) : {});

const str = (doc: Doc, key: string): string | undefined => {
  const value = doc[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const num = (doc: Doc, key: string): number | undefined => {
  const value = doc[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const strings = (doc: Doc, key: string, separator = ', '): string | undefined => {
  const value = doc[key];
  return Array.isArray(value) && value.length > 0
    ? value.filter((v) => typeof v === 'string').join(separator)
    : undefined;
};

const path = (...segments: readonly (string | number)[]): string =>
  '/' + segments.map((segment) => encodeURIComponent(String(segment))).join('/');

const field =
  (key: string) =>
  (doc: Doc): FieldValue =>
    str(doc, key) ?? num(doc, key) ?? null;

const joinParts = (parts: readonly (string | undefined)[], separator: string): string | undefined => {
  const present = parts.filter((part): part is string => part !== undefined);
  return present.length > 0 ? present.join(separator) : undefined;
};

const forCounterparty = (base: string, doc: Doc): string => {
  const counterparty = str(doc, 'counterpartyName');
  return counterparty ? `${base} for ${counterparty}` : base;
};

const ORDER_TYPES: Readonly<Record<string, { readonly label: string; readonly segment: string }>> = {
  SALES_ORDER: { label: 'SALES ORDER', segment: 'sales-orders' },
  PURCHASE_ORDER: { label: 'PURCHASE ORDER', segment: 'purchase-orders' },
  BUYSELL_ORDER: { label: 'BUY-SELL', segment: 'buy-sell-orders' },
  WORK_ORDER: { label: 'GROWER PRODUCT', segment: 'grower-products' },
};

const orderType = (doc: Doc): { readonly label: string; readonly segment: string } | undefined =>
  ORDER_TYPES[str(doc, 'orderType') ?? ''];

const billTypeLabel = (doc: Doc): string => (str(doc, 'billType') === 'PAYABLE' ? 'PAYABLE' : 'INVOICE');

const withListFallback =
  (key: string, segments: readonly string[], listSegments: readonly string[]) =>
  (doc: Doc): string => {
    const slug = str(doc, key);
    return slug ? path(...segments, slug) : path(...listSegments);
  };

const ENTITY_SPECS: Readonly<Record<string, EntitySpec>> = {
  order: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'slug'),
    subtitle: (doc) => joinParts([orderType(doc)?.label ?? 'ORDER', str(doc, 'counterpartyName')], ' · '),
    label: (title, doc) => forCounterparty(`Order #${title}`, doc),
    href: (doc) => {
      const type = orderType(doc);
      const slug = str(doc, 'slug');
      return type && slug ? path(type.segment, slug) : '/';
    },
    fields: {
      orderNumber: field('slug'),
      type: field('orderType'),
      status: field('status'),
      counterparty: field('counterpartyName'),
      reference: field('reference'),
      customerPO: field('customerPO'),
      vendorSO: field('vendorSO'),
    },
  },
  product: {
    idKey: 'productId',
    title: (doc) => str(doc, 'productCode') ?? str(doc, 'description'),
    subtitle: (doc) => (str(doc, 'productCode') ? str(doc, 'description') : undefined),
    label: (title, doc) => joinParts(['Product', title, str(doc, 'description')], ' ') ?? 'Product',
    href: () => '/inventory',
    fields: {
      productCode: field('productCode'),
      description: field('description'),
      commodity: field('commodity'),
      countryOfOrigin: field('countryOfOrigin'),
    },
  },
  lot: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'lotNumber'),
    subtitle: (doc) => str(doc, 'commodity'),
    label: (title) => `Lot ${title}`,
    href: (doc) => {
      const warehouseId = num(doc, 'warehouseId');
      const lotNumber = str(doc, 'lotNumber');
      return warehouseId !== undefined && lotNumber
        ? path('inventory', 'lot-tracking', warehouseId, lotNumber)
        : '/inventory';
    },
    fields: {
      lotNumber: field('lotNumber'),
      commodity: field('commodity'),
      status: field('status'),
      warehouseId: field('warehouseId'),
    },
  },
  productionRun: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'reference'),
    subtitle: () => undefined,
    label: (title) => `Production Run ${title}`,
    href: withListFallback('reference', ['inventory', 'production-runs'], ['inventory', 'production-runs']),
    fields: { reference: field('reference'), status: field('status') },
  },
  bill: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'billNumber'),
    subtitle: (doc) => joinParts([billTypeLabel(doc), str(doc, 'counterpartyName')], ' · '),
    label: (title, doc) => forCounterparty(`${billTypeLabel(doc) === 'PAYABLE' ? 'Payable' : 'Invoice'} ${title}`, doc),
    href: withListFallback('billNumber', ['accounting', 'bill'], ['accounting', 'invoices']),
    fields: {
      billNumber: field('billNumber'),
      type: field('billType'),
      status: field('status'),
      counterparty: field('counterpartyName'),
      reference: field('reference'),
    },
  },
  payment: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'reference'),
    subtitle: (doc) => str(doc, 'counterpartyName'),
    label: (title, doc) => forCounterparty(`Payment ${title}`, doc),
    href: withListFallback('reference', ['accounting', 'payments'], ['accounting', 'payments']),
    fields: { reference: field('reference'), status: field('status'), counterparty: field('counterpartyName') },
  },
  inventoryTransfer: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'reference'),
    subtitle: () => undefined,
    label: (title) => `Transfer ${title}`,
    href: withListFallback('reference', ['inventory', 'transfers'], ['inventory', 'transfers']),
    fields: { reference: field('reference'), status: field('status') },
  },
  receivingTicket: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'slug'),
    subtitle: (doc) => str(doc, 'counterpartyName'),
    label: (title, doc) => forCounterparty(`Receiving Ticket ${title}`, doc),
    href: withListFallback('slug', ['inventory', 'receiving-tickets'], ['inventory', 'receiving-tickets']),
    fields: {
      slug: field('slug'),
      status: field('status'),
      counterparty: field('counterpartyName'),
      reference: field('reference'),
    },
  },
  pickTicket: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'slug'),
    subtitle: (doc) => str(doc, 'counterpartyName'),
    label: (title, doc) => forCounterparty(`Pick Ticket ${title}`, doc),
    href: withListFallback('slug', ['inventory', 'pick-tickets'], ['inventory', 'pick-tickets']),
    fields: {
      slug: field('slug'),
      status: field('status'),
      counterparty: field('counterpartyName'),
      reference: field('reference'),
    },
  },
  ledgerAccount: {
    idKey: 'sourceId',
    title: (doc) => str(doc, 'accountNumber') ?? str(doc, 'name'),
    subtitle: (doc) => (str(doc, 'accountNumber') ? str(doc, 'name') : undefined),
    label: (title, doc) =>
      (str(doc, 'accountNumber') ? joinParts(['Account', title, str(doc, 'name')], ' ') : `Account ${title}`) ??
      'Account',
    href: (doc) => {
      const accountPath = str(doc, 'accountPath');
      return accountPath
        ? path('accounting', 'accounts', 'register', accountPath)
        : path('accounting', 'accounts', 'register');
    },
    fields: {
      name: field('name'),
      accountNumber: field('accountNumber'),
      accountPath: field('accountPath'),
      accountType: field('accountType'),
    },
  },
  businessEntity: {
    idKey: 'businessEntityId',
    title: (doc) => str(doc, 'name'),
    subtitle: (doc) => strings(doc, 'roles'),
    label: (title) => `Business Entity ${title}`,
    href: (doc) => {
      const id = num(doc, 'businessEntityId');
      return id === undefined ? '/' : path('settings', 'business', 'entities', id);
    },
    fields: { name: field('name'), roles: (doc) => strings(doc, 'roles', ',') ?? null },
  },
};

const resolve = (entity: string, raw: unknown): { spec: EntitySpec; doc: Doc; id: number; title: string } => {
  const spec = ENTITY_SPECS[entity];
  const doc = asDoc(raw);
  const id = spec === undefined ? undefined : num(doc, spec.idKey);
  if (spec === undefined || id === undefined) {
    throw new SystemError({
      code: 'SEARCH_DOCUMENT_UNMAPPABLE',
      userMessage: `Cannot map a ${entity} search document without a numeric ${spec?.idKey ?? 'id'}`,
      retryable: false,
    });
  }

  return { spec, doc, id, title: spec.title(doc) ?? String(id) };
};

const rowMapper =
  (entity: string) =>
  (raw: unknown): SearchRow => {
    const { spec, doc, id, title } = resolve(entity, raw);
    const subtitle = spec.subtitle(doc);
    return { entity, id, title, ...(subtitle === undefined ? {} : { subtitle }), href: spec.href(doc) };
  };

const agentMapper =
  (entity: string) =>
  (raw: unknown): AgentRow => {
    const { spec, doc, id, title } = resolve(entity, raw);
    return {
      entity,
      id,
      label: spec.label(title, doc),
      matchSource: 'keyword',
      fields: Object.fromEntries(Object.entries(spec.fields).map(([key, read]) => [key, read(doc)])),
    };
  };

const entities = Object.keys(ENTITY_SPECS);

export const toSearchRow: Record<string, (doc: unknown) => SearchRow> = Object.fromEntries(
  entities.map((entity) => [entity, rowMapper(entity)])
);

export const toAgentRow: Record<string, (doc: unknown) => AgentRow> = Object.fromEntries(
  entities.map((entity) => [entity, agentMapper(entity)])
);
